<template>
  <figure v-if="item.type === 'image'">
    <img :src="`${publicPath}${content.url}`" alt="" />
    <figcaption v-if="content.description" v-text="content.description" />
  </figure>
  <div v-else-if="item.type === 'youtube'" class="youtube">
    <div class="iframe-wrapper">
      <iframe
        :src="`https://www.youtube.com/embed/${content.youtubeId}`"
        width="560"
        height="315"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      />
    </div>
    <p v-if="content.description" v-text="content.description" />
  </div>
  <div v-else-if="item.type === 'swiper'" class="swiper">
    <swiper
      :slides-per-view="1"
      :autoplay="{
        delay: 3000,
      }"
      navigation
    >
      <swiper-slide v-for="(item, index) in content" :key="`swiper${index}`">
        <figure>
          <img :src="`${publicPath}${item.url}`" :alt="item.description" />
          <figcaption v-if="item.description" v-text="item.description" />
        </figure>
      </swiper-slide>
    </swiper>
  </div>
  <h2 v-else-if="item.type === 'heading'" v-text="content" />
  <p v-else v-text="content" />
</template>

<script>
import SwiperCore, { Autoplay, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';

import 'swiper/swiper.scss';

SwiperCore.use([Autoplay, Navigation]);

export default {
  name: 'ContentHandler',

  components: {
    Swiper,
    SwiperSlide,
  },

  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },

  data () {
    return {
      publicPath: process.env.BASE_URL
    }
  },

  computed: {
    content() {
      return this.item?.content ?? {}
    }
  },
}
</script>

<style lang="scss" scoped>
$colors: (
  'white': #ffffff,
  'black': #000000,
) !default;

.swiper {
  &:deep {
    :root {
      --swiper-navigation-size: 44px;
      /*
      --swiper-navigation-color: var(--swiper-theme-color);
      */
    }
    .swiper-button-prev,
    .swiper-button-next {
      position: absolute;
      top: 50%;
      width: calc(var(--swiper-navigation-size) / 44 * 27);
      height: var(--swiper-navigation-size);
      margin-top: calc(-1 * var(--swiper-navigation-size) / 2);
      z-index: 10;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--swiper-navigation-color, var(--swiper-theme-color));
      &.swiper-button-disabled {
        opacity: 0.35;
        cursor: auto;
        pointer-events: none;
      }
      &:after {
        font-family: swiper-icons;
        font-size: var(--swiper-navigation-size);
        text-transform: none !important;
        letter-spacing: 0;
        text-transform: none;
        font-variant: initial;
        line-height: 1;
      }
    }
    .swiper-button-prev,
    .swiper-container-rtl .swiper-button-next {
      &:after {
        content: 'prev';
      }
      left: 10px;
      right: auto;
    }
    .swiper-button-next,
    .swiper-container-rtl .swiper-button-prev {
      &:after {
        content: 'next';
      }
      right: 10px;
      left: auto;
    }

    @each $navColorName, $navColorValue in $colors {
      .swiper-button-prev,
      .swiper-button-next {
        &.swiper-button-#{$navColorName} {
          --swiper-navigation-color: #{$navColorValue};
        }
      }
    }
    .swiper-button-lock {
      display: none;
    }
  }
}
</style>
<style lang="scss" scoped>
.article-body  {
  h2 {
    font-size: 28px;
    font-family: source-han-serif-tc, 'Songti TC', serif;
    font-weight: 700;
    line-height: 1.5;
    margin-top: 60px;
    @media screen and (min-width: 996px) {
      font-size: 30px;
    }
  }

  figure {
    width: 100%;
    padding-left: 0;
    padding-right: 0;

    img {
      width: 100%;
      @media screen and (min-width: 996px) {
        border-radius: 4px;
      }
    }

    figcaption {
      padding-top: 10px;
      margin-top: 12px;
      margin-left: 17px;
      margin-right: 17px;
      color: rgba(#000, 0.66);
      font-size: 13px;
      line-height: 1.5;
      letter-spacing: 1px;
      border-top: 1px solid #979797;
      @media screen and (min-width: 996px) {
        margin-top: 16px;
        margin-left: 33px;
        margin-right: 33px;
      }
    }
  }

  .youtube {
    padding: 0;
    .iframe-wrapper {
      position: relative;
      padding: 56.25% 0 0;
      overflow: hidden;
      iframe {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center center;
      }
    }
    
    p {
      padding-top: 10px;
      margin-top: 12px;
      margin-left: 17px;
      margin-right: 17px;
      color: rgba(#000, 0.66);
      font-size: 13px;
      line-height: 1.5;
      letter-spacing: 1px;
      border-top: 1px solid #979797;
      @media screen and (min-width: 996px) {
        margin-top: 16px;
        margin-left: 33px;
        margin-right: 33px;
      }
    }
  }

  .swiper {
    @media screen and (min-width: 996px) {
      padding: 0;
    }
    &:deep {
      .swiper-button-prev, .swiper-button-next {
        top: calc(50% - 25px);
        color: #fff;
        transform: translateY(-50%);
        &:after {
          font-size: 22px;
          @media screen and (min-width: 768px) {
            font-size: 35px;
          }
        }
      }
    }
  }
}
</style>